import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslations } from 'use-intl';
import { Loader2 } from 'lucide-react';
import {
  FEEDBACK_MAX_MESSAGE_LENGTH,
  isFeedbackMessageValid,
  isFeedbackMessageTooLong,
  shouldShowFeedbackCounter,
  sanitizeFeedbackContext,
  mintClientSubmissionId,
  // FIN-869: only packages/ui's pure feedback/core.ts, via its own subpath export — never the
  // package root, which would also pull in packages/ui's React components.
} from '@financialharmony/ui/feedback/core';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useCompany } from '@/hooks/useCompany';
import { useFeedbackContextApi } from '@/contexts/useFeedbackContext';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { FeedbackApiError } from '@/clients/feedbackApiClient';
import { getFeedbackSentryIds } from '@/lib/feedback/feedbackSentryIds';
import { StartShowAndTell } from '@/components/feedback/showAndTell/StartShowAndTell';
import { OPEN_FEEDBACK_EVENT } from '@/lib/showAndTell/openFeedbackEvent';
function draftKey(userId: string): string {
  // Keyed by user id (never a bare constant) — a shared computer with a different signed-in
  // user must never see the previous user's draft. Mirrors apps/client/lib/feedback/draft.ts.
  return `harmony:feedback:draft:${userId}`;
}

/** Wrapped in try/catch — sessionStorage can throw (private browsing, quota, disabled storage)
 * and that must never block typing or sending (EC17). */
function saveDraft(userId: string, message: string): void {
  try {
    if (message.trim().length === 0) {
      window.sessionStorage.removeItem(draftKey(userId));
      return;
    }
    window.sessionStorage.setItem(draftKey(userId), message);
  } catch {
    // Never blocks the panel.
  }
}

function loadDraft(userId: string): string | null {
  try {
    return window.sessionStorage.getItem(draftKey(userId));
  } catch {
    return null;
  }
}

function clearDraft(userId: string): void {
  try {
    window.sessionStorage.removeItem(draftKey(userId));
  } catch {
    // Never blocks the panel.
  }
}

function MessageCounter({ length, tooLong }: { length: number; tooLong: boolean }) {
  const t = useTranslations('feedback');
  return (
    <p
      aria-live="polite"
      className={cn('text-xs text-right', tooLong ? 'text-destructive' : 'text-muted-foreground')}
    >
      {t('counter', { length: String(length), max: String(FEEDBACK_MAX_MESSAGE_LENGTH), tooLong: tooLong ? 'yes' : 'no' })}
    </p>
  );
}

export interface FeedbackPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * screens.md S3: a right-side Sheet with no scrim, read-only chips for company/page/record, and
 * the same states as S2 (client portal's FeedbackDialog) including draft restored (EC17).
 * FIN-869 (in-app-feedback stack PR6).
 */
export function FeedbackPanel({ open, onOpenChange }: FeedbackPanelProps) {
  const t = useTranslations('feedback');
  const ta = useTranslations('consultantOnboarding.admin.feedback');
  const { user } = useAuth();
  const { company } = useCompany();
  const location = useLocation();
  const navigate = useNavigate();
  const feedbackContextApi = useFeedbackContextApi();

  const [message, setMessage] = useState('');
  const [draftRestored, setDraftRestored] = useState(false);
  const [recordChips, setRecordChips] = useState<Record<string, string>>({});
  const [isSending, setIsSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const [submitReference, setSubmitReference] = useState<string | undefined>(undefined);
  const [rateLimited, setRateLimited] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const [clientSubmissionId, setClientSubmissionId] = useState('');
  const [copied, setCopied] = useState(false);

  const userId = user?.id;

  // The modal launcher and Alt+Shift+F live outside the layouts, so they ask this panel to open.
  useEffect(() => {
    const openPanel = () => onOpenChange(true);
    window.addEventListener(OPEN_FEEDBACK_EVENT, openPanel);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, openPanel);
  }, [onOpenChange]);

  // "Adjust state during render" (React's own pattern, not a useEffect — avoids
  // react-hooks/set-state-in-effect) resets transient state each time the panel opens or the
  // user changes. prevOpen starts false (not from `open`) so a component that mounts already
  // open still runs the reset.
  const [prevOpen, setPrevOpen] = useState(false);
  const [prevUserId, setPrevUserId] = useState(userId);
  if (open !== prevOpen || userId !== prevUserId) {
    setPrevOpen(open);
    setPrevUserId(userId);
    if (open) {
      setSendFailed(false);
      setSubmitReference(undefined);
      setRateLimited(false);
      setSignedOut(false);
      setCopied(false);
      setClientSubmissionId(mintClientSubmissionId());
      setRecordChips(sanitizeFeedbackContext(feedbackContextApi?.getContext() ?? {}));

      const draft = userId ? loadDraft(userId) : null;
      if (draft && draft.trim().length > 0) {
        setMessage(draft);
        setDraftRestored(true);
      } else {
        setMessage('');
        setDraftRestored(false);
      }
    }
  }

  const tooLong = isFeedbackMessageTooLong(message);
  const valid = isFeedbackMessageValid(message);
  const showCounter = shouldShowFeedbackCounter(message);

  function handleMessageChange(next: string) {
    setMessage(next);
    setDraftRestored(false);
    if (userId) saveDraft(userId, next);
  }

  // The typed text travels with the recording as its note, so the unsent draft must not come back
  // as "Draft restored" after the recording is sent.
  function handleRecordingStarted() {
    if (userId) clearDraft(userId);
    onOpenChange(false);
  }

  function handleClear() {
    // handleMessageChange('') already clears the persisted draft (saveDraft's empty-message
    // branch) — no separate clearDraft call needed here.
    handleMessageChange('');
  }

  async function handleSend() {
    if (!valid || isSending) return;
    setIsSending(true);
    setSendFailed(false);
    setSubmitReference(undefined);
    setRateLimited(false);
    setSignedOut(false);
    try {
      const sentryIds = await getFeedbackSentryIds();
      await ServiceRegistry.getFeedbackService().submitFeedback({
        message,
        clientSubmissionId,
        route: location.pathname,
        // Device keys first (F6/AC3) — sanitizeFeedbackContext's 50-key cap drops from the end
        // of insertion order, so a page contributing many keys must never push these out.
        // recordChips is the chip snapshot already on screen, not a fresh getContext() read —
        // what the consultant sees attached is exactly what gets sent.
        context: {
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          locale: navigator.language,
          ...recordChips,
        },
        sentryEventId: sentryIds.sentryEventId ?? undefined,
        sentryReplayId: sentryIds.sentryReplayId ?? undefined,
      });
      if (userId) clearDraft(userId);
      onOpenChange(false);
      toast.success(t('thanks'));
    } catch (error) {
      if (error instanceof FeedbackApiError) {
        if (error.code === 'RATE_LIMITED') {
          setRateLimited(true);
        } else if (error.code === 'UNAUTHORIZED') {
          setSignedOut(true);
        } else {
          // Never error.message here — can be a raw Postgres/server string for an unclassified failure.
          setSendFailed(true);
          setSubmitReference(error.reference);
        }
      } else {
        setSendFailed(true);
      }
    } finally {
      setIsSending(false);
    }
  }

  function handleCopyText() {
    navigator.clipboard.writeText(message)
      .then(() => setCopied(true))
      .catch(() => {});
  }

  // The stale local session must be cleared first — otherwise /auth's own already-signed-in
  // check bounces straight back to /overview instead of showing the login form.
  async function handleSignIn() {
    await supabase.auth.signOut({ scope: 'local' });
    navigate('/auth', { state: { from: location } });
  }

  // Blocks Escape/overlay-click closes while a submit is in flight (EC6 — no accidental close
  // mid-send). Radix's onOpenChange fires for both close paths regardless of a button's own
  // disabled state.
  function handleOpenChange(next: boolean) {
    if (isSending && !next) return;
    onOpenChange(next);
  }

  const recordChipEntries = Object.entries(recordChips);
  const trimmedLength = message.trim().length;

  let sendButtonLabel: ReactNode = t('send');
  if (isSending) {
    sendButtonLabel = (
      <>
        <Loader2 className="h-4 w-4 animate-spin" />
        {ta('sending')}
      </>
    );
  } else if (sendFailed) {
    sendButtonLabel = t('tryAgain');
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent hideOverlay data-testid="feedback-panel" data-feedback-panel className="flex flex-col gap-4 sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t('title')}</SheetTitle>
          <SheetDescription>{ta('description')}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-wrap gap-2" data-testid="feedback-context-chips">
          <Badge variant="outline">{ta('company', { name: company?.name ?? ta('noCompany') })}</Badge>
          <Badge variant="outline">{ta('page', { path: location.pathname })}</Badge>
          {recordChipEntries.map(([key, value]) => (
            <Badge key={key} variant="outline">{key}: {value}</Badge>
          ))}
        </div>

        <div className="flex flex-col gap-3 flex-1">
          {draftRestored && (
            <Alert>
              <AlertTitle>{t('draftRestored')}</AlertTitle>
              <AlertDescription>
                <Button variant="ghost" size="sm" onClick={handleClear}>{t('clear')}</Button>
              </AlertDescription>
            </Alert>
          )}
          {signedOut && (
            <Alert>
              <AlertTitle>{t('signedOutTitle')}</AlertTitle>
              <AlertDescription>
                <div className="flex flex-col gap-2">
                  <p>{t('signedOutBody')}</p>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={handleCopyText}>
                      {copied ? ta('copied') : t('copyText')}
                    </Button>
                    <Button variant="default" size="sm" onClick={handleSignIn}>{t('signIn')}</Button>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}
          {rateLimited && (
            <Alert>
              <AlertTitle>{t('rateLimitedTitle')}</AlertTitle>
              <AlertDescription>{t('rateLimitedBody')}</AlertDescription>
            </Alert>
          )}
          {sendFailed && (
            <Alert variant="destructive">
              <AlertTitle>{t('sendFailedTitle')}</AlertTitle>
              <AlertDescription>
                {t('sendFailedFallback')}
                {submitReference && ` ${ta('reference', { reference: submitReference })}`}
              </AlertDescription>
            </Alert>
          )}
          <StartShowAndTell note={message} onStarted={handleRecordingStarted} />
          <Textarea
            autoFocus
            rows={8}
            placeholder={t('placeholder')}
            value={message}
            onChange={(e) => handleMessageChange(e.target.value)}
            readOnly={isSending}
            aria-label={t('messageLabel')}
          />
          <p className="text-xs text-muted-foreground">
            {t('noPersonalData')}
          </p>
          {showCounter && <MessageCounter length={trimmedLength} tooLong={tooLong} />}
        </div>

        <SheetFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)} disabled={isSending}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSend} disabled={!valid || isSending}>
            {sendButtonLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
